import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Column } from 'features/Yucollect/utils/types';

import {
  IFailedPayload,
  IGetAgentListRequest,
  IGetAgentListResponse,
  IGetAgentsCountResponse,
  IGetUniqueValuesByColumnRequest,
  IGetUniqueValuesByColumnResponse,
  IHeaders,
  ISearchAgentListRequest,
} from '../../types/Agent.types';

export interface AgentListState {
  // TODO: Right now pagination is not properly handled
  data: IGetAgentListResponse | null;
  headers: IHeaders[];
  loading: boolean;
  isAgentListLoading: boolean;
  error: IFailedPayload | null;
  uniqueValues: string[];
  filteredResultsByValues: string[];
  bucket: string | null;
  channel: string | null;
  pagination: {
    limit: number;
    offset: number;
    totalCount: number;
    page: number;
    pageSize: number;
  };
  agent_id: string | null;
  agent_name: string | null;
  invalid_records: number;
  no_follow_up_record: number;
  totalRecords: number;
  filters: Column[];
  appliedFilters: any;
  agentListRecordIds: string[];
  agentsCount: IGetAgentsCountResponse | null;
  isOffboardingAgent: boolean;
  isUnblockingUser: boolean;
  unblockUserResult: 'idle' | 'success' | 'error';
  unblockUserErrorMsg: string;
}

export const agentDataInitState: AgentListState = {
  data: null,
  uniqueValues: [],
  filteredResultsByValues: [],
  loading: false,
  isAgentListLoading: false,
  error: null,
  bucket: null,
  channel: null,
  pagination: {
    limit: 10,
    offset: 0,
    totalCount: 0,
    page: 1,
    pageSize: 10,
  },
  agent_id: null,
  agent_name: null,
  invalid_records: 0,
  no_follow_up_record: 0,
  headers: [],
  filters: [],
  appliedFilters: {},
  totalRecords: 0,
  agentListRecordIds: [],
  agentsCount: null,
  isOffboardingAgent: false,
  isUnblockingUser: false,
  unblockUserResult: 'idle',
  unblockUserErrorMsg: '',
};

const agentListSlice = createSlice({
  name: 'agentList',
  initialState: agentDataInitState,
  reducers: {
    // Data grid actions
    fetchAgentRecordsByAgencyId: (
      state,
      _action: PayloadAction<IGetAgentListRequest>
    ) => {
      state.isAgentListLoading = true;
      state.error = null;
    },
    fetchAgentRecordsByAgencyIdSuccess: (
      state,
      action: PayloadAction<IGetAgentListResponse>
    ) => {
      state.data = action.payload;
      state.totalRecords = action.payload.totalRecords;
      state.isAgentListLoading = false;
    },
    fetchAgentRecordsByAgencyIdFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.isAgentListLoading = false;
    },
    // Search actions
    searchAgentRecords: (
      state,
      action: PayloadAction<ISearchAgentListRequest>
    ) => {
      state.isAgentListLoading = true;
      state.error = null;
    },
    searchAgentRecordsSuccess: (
      state,
      action: PayloadAction<IGetAgentListResponse>
    ) => {
      state.data = action.payload;
      state.totalRecords = action.payload.totalRecords;
      state.isAgentListLoading = false;
    },
    searchAgentRecordsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.isAgentListLoading = false;
    },
    getUniqueValuesByColumnName: (
      state,
      action: PayloadAction<IGetUniqueValuesByColumnRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getUniqueValuesByColumnNameSuccess: (
      state,
      action: PayloadAction<IGetUniqueValuesByColumnResponse>
    ) => {
      state.uniqueValues = action.payload;
      state.loading = false;
    },
    getUniqueValuesByColumnNameFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    setAgentListRecordIds: (state, action) => {
      state.agentListRecordIds = action.payload;
    },
    // Get agents count
    getAgentsCount: (state) => {
      state.loading = true;
      state.error = null;
    },
    getAgentsCountSuccess: (
      state,
      action: PayloadAction<IGetAgentsCountResponse>
    ) => {
      state.agentsCount = action.payload;
      state.loading = false;
    },
    getAgentsCountFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    offboardAgent: (state, action: PayloadAction<{ agentId: string }>) => {
      state.isOffboardingAgent = true;
      state.error = null;
    },
    offboardAgentSuccess: (state) => {
      state.isOffboardingAgent = false;
    },
    offboardAgentFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.isOffboardingAgent = false;
    },
    unblockUser: (
      state,
      _action: PayloadAction<{ userId: string; agentName: string }>
    ) => {
      state.isUnblockingUser = true;
      state.error = null;
    },
    unblockUserSuccess: (state) => {
      state.isUnblockingUser = false;
      state.unblockUserResult = 'success';
    },
    unblockUserFailure: (
      state,
      action: PayloadAction<IFailedPayload & { errorMsg?: string }>
    ) => {
      state.error = action.payload;
      state.isUnblockingUser = false;
      state.unblockUserResult = 'error';
      state.unblockUserErrorMsg =
        action.payload.errorMsg || 'Failed to unblock user';
    },
    resetUnblockUserResult: (state) => {
      state.unblockUserResult = 'idle';
      state.unblockUserErrorMsg = '';
    },
    saveAppliedFilters: (state, action: PayloadAction<any>) => {
      const newFilters = action.payload;
      state.appliedFilters = {
        ...state.appliedFilters,
        ...newFilters,
      };
      state.loading = false;
    },
  },
});

export const {
  fetchAgentRecordsByAgencyId,
  fetchAgentRecordsByAgencyIdSuccess,
  fetchAgentRecordsByAgencyIdFailure,
  getUniqueValuesByColumnName,
  getUniqueValuesByColumnNameSuccess,
  getUniqueValuesByColumnNameFailure,
  setAgentListRecordIds,
  getAgentsCount,
  getAgentsCountSuccess,
  getAgentsCountFailure,
  saveAppliedFilters,
  searchAgentRecords,
  searchAgentRecordsSuccess,
  searchAgentRecordsFailure,
  offboardAgent,
  offboardAgentSuccess,
  offboardAgentFailure,
  unblockUser,
  unblockUserSuccess,
  unblockUserFailure,
  resetUnblockUserResult,
} = agentListSlice.actions;

export default agentListSlice.reducer;
